import * as React from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';
import logoImage from '../../assets/Chattitude_Main_Logo_v5.svg';
import { useReseller } from '../../contexts/ResellerContext';
import { useResellerTheme } from './ThemeWrapper';
import { BASE_URL } from '../../common/Constants';
import { Paper, Button, Chip, Alert, Snackbar, Select, MenuItem, FormControl } from '@mui/material';
import Cookies from '../../common/Cookies';
import HouseOutlined from "@mui/icons-material/HouseOutlined";
import SmartToyOutlined from "@mui/icons-material/SmartToyOutlined";
import PsychologyAltOutlined from "@mui/icons-material/PsychologyAltOutlined";
import IntegrationInstructionsOutlined from "@mui/icons-material/IntegrationInstructionsOutlined";
import BuildOutlined from "@mui/icons-material/BuildOutlined";
import HeadsetMicOutlined from "@mui/icons-material/HeadsetMicOutlined";
import SettingsOutlined from "@mui/icons-material/SettingsOutlined";
import NotificationsActiveOutlined from "@mui/icons-material/NotificationsActiveOutlined";
import AssessmentOutlined from '@mui/icons-material/AssessmentOutlined';
import HelpCenterOutlined from "@mui/icons-material/HelpCenterOutlined";
import LogoutOutlined from "@mui/icons-material/LogoutOutlined";
import { AppProvider } from '@toolpad/core/AppProvider';
import { useGlobalAlerts } from '../../contexts/AlertContext';
import { useAuth, userFromDecodedJwt, shouldShowAccountSettings } from '../../contexts/AuthContext';
import { useUserNotifications } from '../../hooks/useUserNotifications';
import './MenuPanel.css';
import { DashboardLayout } from '@toolpad/core/DashboardLayout';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AlertProvider, } from '../../contexts/AlertContext';
import ListAltIcon from '@mui/icons-material/ListAlt';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import UserProfileModal from '../account/UserProfileModal';
import QuestionAnswerOutlinedIcon from '@mui/icons-material/QuestionAnswerOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import Badge from '@mui/material/Badge';

// Custom logo component - uses reseller logo when available
function CustomLogo() {
  const { reseller } = useReseller();
  const logoSrc = reseller?.logoPath
    ? reseller.logoPath.startsWith('http://') || reseller.logoPath.startsWith('https://')
      ? reseller.logoPath
      : `${BASE_URL}/api/public/reseller/${reseller.id}/logo`
    : logoImage;
  return (
    <img
      src={logoSrc}
      alt={reseller?.name || 'Chattitude Logo'}
      style={{ height: '32px', width: 'auto', marginRight: '10px', cursor: 'pointer' }}
      onError={(e) => {
        e.target.onerror = null;
        e.target.src = logoImage;
      }}
    />
  );
}

// Map system status to color (same as SystemAlerts)
function getStatusColor(status) {
  switch ((status || '').toLowerCase()) {
    case 'operational': return '#4caf50';
    case 'degraded': return '#ffb300';
    case 'outage': return '#ff1744';
    case 'maintenance': return '#2196f3';
    default: return 'inherit';
  }
}


export default function MenuPanel({ children }) {
  // System status for System Alerts icon
  const [systemStatus, setSystemStatus] = React.useState(null);
  React.useEffect(() => {
    let isMounted = true;
    const fetchStatus = () => {
      axios.get('/system/health')
        .then(res => { if (isMounted) setSystemStatus(res.data?.systemStatus); })
        .catch(() => { if (isMounted) setSystemStatus(null); });
    };
    fetchStatus();
    const interval = setInterval(fetchStatus, 30000); // poll every 30 seconds
    return () => { isMounted = false; clearInterval(interval); };
  }, []);
  // Keep modal state in a stable parent so it survives child remounts
  const [profileOpen, setProfileOpen] = React.useState(false);
  const { user, setUser, isLoading } = useAuth();
  const { hasNewChatNotification, clearNewChatNotification } = useUserNotifications();

  // Agency tenant switcher state
  const [agencyTenants, setAgencyTenants] = React.useState([]);
  const currentTenantId = user?.tenantId || Cookies.getTenantId();

  React.useEffect(() => {
    if (!user?.agencyId) return;
    axios.get('/login/agency-tenants')
      .then(res => {
        if (res.data?.status === 'success' && Array.isArray(res.data.body)) {
          setAgencyTenants(res.data.body);
        }
      })
      .catch(() => { });
  }, [user?.agencyId]);

  const handleTenantSwitch = React.useCallback((e) => {
    const newTenantId = e.target.value;
    if (String(newTenantId) === String(currentTenantId)) return;
    axios.post('/login/switch-tenant', null, { params: { tenantId: newTenantId } })
      .then(res => {
        if (res.data?.status === 'success') {
          const jwtToken = res.data.body;
          Cookies.setJwtToken(jwtToken);
          axios.defaults.headers.common['Authorization'] = `Bearer ${jwtToken}`;
          const payload = JSON.parse(atob(jwtToken.split('.')[1]));
          Cookies.setTenantId(payload.tenantId);
          Cookies.setCookies('hasAgent', payload.hasAgent);
          setUser(userFromDecodedJwt(payload));
          setTimeout(() => {
            window.location.hash = '#/Dashboard';
            window.location.reload();
          }, 100);
        }
      })
      .catch(() => { });
  }, [currentTenantId, setUser]);

  // Debug log to see if notification state is updating
  React.useEffect(() => {
    console.log('MenuPanel: hasNewChatNotification changed to:', hasNewChatNotification);
  }, [hasNewChatNotification]);

  React.useEffect(() => {
    // keep profileOpen effect for potential future use
  }, [profileOpen]);

  const chatLogAccessOnly = !!user?.chat_log_access_only;

  const NAVIGATION_FULL = React.useMemo(() => [
    {
      segment: '#/Dashboard',
      title: 'Home',
      icon: <HouseOutlined />
    },
    {
      segment: '#/Agents',
      title: 'Agents',
      icon: <SmartToyOutlined />
    },
    {
      segment: '#/KnowledgeList',
      title: 'Knowledge',
      icon: <PsychologyAltOutlined />
    },
    {
      segment: '#/Instructions',
      title: 'Instructions',
      icon: <IntegrationInstructionsOutlined />
    },
    {
      segment: '#/FormsList',
      title: 'Forms',
      icon: <ListAltIcon />
    },
    {
      segment: '#/chat-logs',
      title: 'Chat Logs',
      icon: <ReceiptLongOutlinedIcon />
    },
    {
      segment: '#/qa',
      title: 'Q&A',
      icon: <QuestionAnswerOutlinedIcon />
    },
    {
      segment: '#/tools',
      title: 'Tools',
      icon: <BuildOutlined />
    },
    {
      segment: '#/live-chat',
      title: hasNewChatNotification ? 'New Chat!' : 'Live Chat',
      icon: hasNewChatNotification ? (
        <Badge color="error" variant="dot" overlap="circular">
          <HeadsetMicOutlined />
        </Badge>
      ) : (
        <HeadsetMicOutlined />
      )
    },
    {
      segment: '#/analytics',
      title: 'Analytics',
      icon: <AssessmentOutlined />
    },
    {
      segment: '#/Logout',
      title: 'Logout',
      icon: <LogoutOutlined />
    }
  ], [hasNewChatNotification]);

  const NAVIGATION_CHAT_LOG_ONLY = React.useMemo(() => [
    {
      segment: '#/Dashboard',
      title: 'Home',
      icon: <HouseOutlined />
    },
    {
      segment: '#/chat-logs',
      title: 'Chat log',
      icon: <ReceiptLongOutlinedIcon />
    },
    {
      segment: '#/Logout',
      title: 'Logout',
      icon: <LogoutOutlined />
    }
  ], []);

  const NAVIGATION = chatLogAccessOnly ? NAVIGATION_CHAT_LOG_ONLY : NAVIGATION_FULL;

  const branding = {
    title: "",
    logo: <CustomLogo />
  };
  // Handle menu item highlighting
  React.useEffect(() => {
    const highlightMenuItem = (item) => {
      const menuItems = document.querySelectorAll('.MuiListItem-root');

      // Reset all menu items
      menuItems.forEach(i => {
        i.classList.remove('Mui-selected');
        i.style.backgroundColor = '';
        i.style.borderLeft = '';

        const text = i.querySelector('.MuiListItemText-primary');
        const icon = i.querySelector('.MuiListItemIcon-root');
        if (text) text.style.color = '';
        if (icon) icon.style.color = '';
      });

      // Highlight the selected item
      item.classList.add('Mui-selected');
      item.style.backgroundColor = '';
      item.style.borderLeft = '';
      const text = item.querySelector('.MuiListItemText-primary');
      const icon = item.querySelector('.MuiListItemIcon-root');
      if (text) text.style.color = '';
      if (icon) icon.style.color = '';

      // Save selection to localStorage
      const link = item.querySelector('a');
      if (link) {
        localStorage.setItem('selectedMenuItem', link.getAttribute('href'));
      }
    };

    const setupMenuItems = () => {
      const menuItems = document.querySelectorAll('.MuiListItem-root');
      if (menuItems.length === 0) {
        // Retry if menu items aren't loaded yet
        setTimeout(setupMenuItems, 500);
        return;
      }

      // Add click handlers
      menuItems.forEach((item, idx) => {
        if (NAVIGATION[idx] && NAVIGATION[idx].onClick) {
          item.addEventListener('click', function (e) {
            e.preventDefault();
            NAVIGATION[idx].onClick(e);
          });
        } else {
          item.addEventListener('click', function () {
            highlightMenuItem(this);
          });
        }
      });

      // Restore selected item from localStorage
      const storedItem = localStorage.getItem('selectedMenuItem');
      let found = false;

      if (storedItem) {
        menuItems.forEach(item => {
          const link = item.querySelector('a');
          if (link && link.getAttribute('href') === storedItem) {
            highlightMenuItem(item);
            found = true;
          }
        });
      }

      // Default to first item if nothing is selected
      if (!found && menuItems.length > 0) {
        highlightMenuItem(menuItems[0]);
      }
    };

    // Initialize after DOM is loaded
    setTimeout(setupMenuItems, 500);

    // Cleanup
    return () => {
      const menuItems = document.querySelectorAll('.MuiListItem-root');
      menuItems.forEach(item => {
        item.removeEventListener('click', () => { });
      });
    };
  }, [NAVIGATION]);


  function TopBarIconsWrapper({ loggedIn, onProfileOpen, hasNewChatNotification, onNewChatClick, agencyTenants, currentTenantId, onTenantSwitch, chatLogAccessOnly: toolbarRestricted }) {
    const navigate = useNavigate();
    const { hasAlerts } = useGlobalAlerts();

    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8, position: 'relative', right: 24, top: 0, zIndex: 100 }}>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {agencyTenants.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select
                value={currentTenantId || ''}
                onChange={onTenantSwitch}
                displayEmpty
                sx={{ fontSize: '0.875rem', height: 36 }}
              >
                {agencyTenants.map((t) => (
                  <MenuItem key={t.id} value={t.id}>{t.tenantName}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {!toolbarRestricted && hasNewChatNotification && (
            <Chip
              icon={<HeadsetMicOutlined />}
              label="New Chat Waiting!"
              onClick={onNewChatClick}
              color="error"
              variant="filled"
              sx={{
                cursor: 'pointer',
                animation: 'blink 1.5s ease-in-out infinite',
                fontWeight: 600,
                '@keyframes blink': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0.7 }
                },
                '&:hover': {
                  opacity: 1,
                  animation: 'none'
                }
              }}
            />
          )}
          <Tooltip title="User Profile">
            <IconButton size="large" onClick={onProfileOpen}>
              <AccountCircleIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Help Center">
            <IconButton
              size="large"
              onClick={() => window.open('/help/', '_blank', 'noopener,noreferrer')}
            >
              <HelpCenterOutlined fontSize="inherit" />
            </IconButton>
          </Tooltip>
          {!toolbarRestricted && shouldShowAccountSettings(user) && (
            <Tooltip title="Account Settings">
              <IconButton size="large" onClick={() => navigate('/AccountSettings')}>
                <SettingsOutlined fontSize="inherit" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="System Alerts">
            <IconButton size="large" onClick={() => navigate('/SystemAlerts')}>
              <NotificationsActiveOutlined
                fontSize="inherit"
                style={{ color: getStatusColor(systemStatus), transition: 'color 0.3s ease' }}
              />
            </IconButton>
          </Tooltip>
        </div>
      </div>
    );
  }

  function CustomToolbarActions() {
    if (isLoading) return null;

    const handleNewChatNotificationClick = () => {
      clearNewChatNotification();
      navigate('/live-chat', { state: { openTab: 0 } });
      window.location.hash = '#/live-chat';
    };

    return (
      <Stack direction="row" alignItems="center">
        <TopBarIconsWrapper
          loggedIn={true}
          onProfileOpen={() => setProfileOpen(true)}
          hasNewChatNotification={hasNewChatNotification}
          onNewChatClick={handleNewChatNotificationClick}
          agencyTenants={agencyTenants}
          currentTenantId={currentTenantId}
          onTenantSwitch={handleTenantSwitch}
          chatLogAccessOnly={chatLogAccessOnly}
        />
        {/* <ThemeSwitcher />  TD:DO Put this later Dark mode issue*/}
      </Stack>
    );
  }

  const { hasAlerts } = useGlobalAlerts();
  const navigate = useNavigate();

  // Update Live Chat menu item onClick to handle navigation and clear notification
  React.useEffect(() => {
    if (chatLogAccessOnly) return;
    const updateLiveChatClickHandler = () => {
      const menuItems = document.querySelectorAll('.MuiListItem-root');
      menuItems.forEach((item, idx) => {
        if (NAVIGATION[idx] && NAVIGATION[idx].segment === '#/live-chat') {
          const link = item.querySelector('a');
          if (link) {
            // Remove existing click listeners by cloning
            const newLink = link.cloneNode(true);
            link.parentNode.replaceChild(newLink, link);

            // Add new click handler
            newLink.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              clearNewChatNotification();
              // Navigate to Live Chat and ensure Open tab is shown
              navigate('/live-chat', { state: { openTab: 0 } });
              window.location.hash = '#/live-chat';
            });

            // Also add blinking animation class if notification is active
            if (hasNewChatNotification) {
              item.classList.add('menu-item-notification');
              const text = item.querySelector('.MuiListItemText-primary');
              if (text) text.classList.add('menu-item-notification');
            } else {
              item.classList.remove('menu-item-notification');
              const text = item.querySelector('.MuiListItemText-primary');
              if (text) text.classList.remove('menu-item-notification');
            }
          }
        }
      });
    };

    // Apply after DOM updates
    const timeoutId = setTimeout(updateLiveChatClickHandler, 300);
    return () => clearTimeout(timeoutId);
  }, [hasNewChatNotification, clearNewChatNotification, navigate, chatLogAccessOnly, NAVIGATION]);

  React.useEffect(() => {
    let audio;
    let intervalId;
    if (hasNewChatNotification && !chatLogAccessOnly) {
      audio = new Audio('/sounds/live_chat_notification.wav');
      audio.loop = true;
      audio.play().catch(() => { });
      // Stop after 15 seconds
      intervalId = setTimeout(() => {
        audio.pause();
        audio.currentTime = 0;
      }, 15000);
    }
    // Cleanup if notification goes away before 15s
    return () => {
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
      if (intervalId) clearTimeout(intervalId);
    };
  }, [hasNewChatNotification, chatLogAccessOnly]);

  const resellerTheme = useResellerTheme();

  return (
    <AppProvider navigation={NAVIGATION} branding={branding} theme={resellerTheme}>
      <DashboardLayout
        key={user?.id || 'anon'}
        slots={{ toolbarActions: CustomToolbarActions }}
        defaultSidebarCollapsed={true}
      >
        <div style={{ padding: "10px 10px 40px 10px", overflow: "auto" }}>
          <Paper elevation={0} style={{ padding: "15px", marginBottom: "20px" }}>
            {children}
          </Paper>
          {/* REMOVE the custom Help button at bottom of sidebar */}
        </div>
      </DashboardLayout>

      {/* Render modal here so it doesn't remount with the toolbar */}
      {
        profileOpen && (
          <UserProfileModal
            open={profileOpen}
            onClose={() => setProfileOpen(false)}
            user={null}
          />
        )
      }
    </AppProvider >
  );
}

MenuPanel.propTypes = {
  children: PropTypes.node,
};
